import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, CircularProgress, Alert,
    IconButton,
    Collapse,
    Table,
    TableBody,
    TableRow,
    TableCell
} from "@mui/material";
import { useZvtTerminal } from "./useZvtTerminal";
import { useTwintPayment } from "./useTwintPayment";
import CashAmountInput from "./CashAmountInput";
import { useTray } from "./useTray";
import { History, PointOfSale } from "@mui/icons-material";

export default function SplitPaymentHandler({ amount, onSuccess, transactionId, onlyCash }) {
    const [cardAmount, setCardAmount] = useState(0);
    const [twintAmount, setTwintAmount] = useState(0);
    const [givenCash, setGivenCash] = useState(0);
    const [paidCard, setPaidCard] = useState(0);
    const [paidTwint, setPaidTwint] = useState(0);
    const [error, setError] = useState(null);

    const { openTray, listDevices } = useTray();

    useEffect(() => {
        listDevices().then(devices => console.log('Available devices:', devices));
    }, [listDevices]);


    const {
        initiatePayment: startCard,
        paymentStatus,
        retryPayment: retryCardPayment
    } = useZvtTerminal({ transactionId });

    const {
        initiateTwintPayment: startTwint,
        retryPayment: retryTwintPayment,
        twintStatus,
        handleReset
    } = useTwintPayment({ transactionId });

    const paidTotal = paidCard + paidTwint;
    const cashRequired = Math.max(0, amount - paidTotal);
    const change = givenCash > cashRequired ? givenCash - cashRequired : 0;
    const isComplete = givenCash >= cashRequired;

    const handleCard = () => {
        setError(null);
        if (cardAmount > 0) {
            startCard(cardAmount);
        } else {
            setError("Bitte Kartenzahlbetrag eingeben.");
        }
    };

    const handleTwint = () => {
        setError(null);
        if (twintAmount > 0) {
            startTwint(Math.round(twintAmount * 100));
        } else {
            setError("Bitte Twint-Betrag eingeben.");
        }
    };

    useEffect(() => {
        if (paymentStatus.code === "SUCCESS") {
            setPaidCard(prev => prev + paymentStatus.amount);
        }
    }, [paymentStatus.code, paymentStatus.amount]);

    useEffect(() => {
        if (twintStatus.code === "SUCCESS") {
            setPaidTwint(prev => prev + twintAmount);
        }
    }, [twintStatus.code]);

    const handleFinish = () => {
        const payments = [];

        if (paidCard > 0) {
            payments.push({ method: "CARD", amount: paidCard });
        }

        if (paidTwint > 0) {
            const foreignAmount = twintStatus?.rate && twintStatus?.currency
                ? (paidTwint * twintStatus.rate)
                : undefined;

            payments.push({
                method: "OTHER",
                amount: paidTwint,
                ...(foreignAmount && {
                    foreign_amount: foreignAmount,
                    foreign_currency: twintStatus.currency
                })
            });
        }

        // 👇 Immer gegebenen Betrag zeigen, nicht nur `cashRequired`
        if (givenCash > 0 || change != 0) {
            payments.push({ method: "CASH", amount: cashRequired, givenCash });
            openTray();
        }

        onSuccess(payments, { givenCash, change });
    };

    useEffect(() => {
        if (isComplete && paidTotal && cashRequired === 0) {
            handleFinish()
        }
    }, [isComplete])

    const isAnyPaymentPending =
        paymentStatus.code === "PENDING" ||
        twintStatus.code === "PENDING";

    const { connected, requestDeviceAndConnect } = useTray();

    const [openPaymentMethods, setOpenPaymentMedthods] = useState({ cash: true, card: true, twint: true })

    const handleOpenPaymentMethod = (k, v = true) => {
        setOpenPaymentMedthods({ ...openPaymentMethods, [k]: v })
    };

    const handleCancelPendingPayments = () => {
        handleReset();
    };


    return (
        <Box sx={{ p: 2, border: "1px solid #ccc", borderRadius: 2, mt: 2 }}>


            {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
            {paymentStatus.code === "ERROR" && (
                <Alert severity="error" sx={{ my: 1 }}>{paymentStatus.msg || "Fehler bei Kartenzahlung"}</Alert>
            )}
            {twintStatus.code === "ERROR" && (
                <Alert severity="error" sx={{ my: 1 }}>{twintStatus.msg || "Fehler bei Twint-Zahlung"}</Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: "end" }}>

                <IconButton onClick={requestDeviceAndConnect}>
                    {connected ? <PointOfSale color="success" /> : <PointOfSale color="error" />}
                </IconButton>
                <IconButton onClick={handleCancelPendingPayments}>
                    {connected ? <History color="success" /> : <History color="error" />}
                </IconButton>
            </Box>

            <Box>
                {!onlyCash && <>

                    <Collapse in={!openPaymentMethods.card}>
                        <Button
                            onClick={() => handleOpenPaymentMethod('card')}
                        >
                            Karte
                        </Button>
                    </Collapse>
                    <Collapse in={openPaymentMethods.card}>
                        <Box>
                            <Box display={"flex"} gap={2} pb={2}>
                                <CashAmountInput
                                    label="KARTE"
                                    max={cashRequired}
                                    onChange={(val) => setCardAmount(val / 100)}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCard}
                                    disabled={isAnyPaymentPending || cardAmount <= 0}
                                    sx={{ flex: 1 }}
                                >
                                    {paymentStatus.code === "PENDING" ? <CircularProgress size={20} /> : "Karten-Zahlung"}
                                </Button>
                            </Box>
                            {paymentStatus.code === "PENDING" && (
                                <Button sx={{ mb: 2 }} variant="outlined" color="warning" size="small" onClick={retryCardPayment}>Karte erneut versuchen</Button>
                            )}
                        </Box>
                    </Collapse>

                    <Collapse in={!openPaymentMethods.twint}>
                        <Button
                            onClick={() => handleOpenPaymentMethod('twint')}
                        >
                            Twint
                        </Button>
                    </Collapse>
                    <Collapse in={openPaymentMethods.twint}>
                        <Box>
                            <Box display={"flex"} gap={2} pb={2}>


                                <CashAmountInput
                                    label="TWINT"
                                    max={cashRequired}
                                    onChange={(val) => setTwintAmount(val / 100)}
                                />
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleTwint}
                                    disabled={isAnyPaymentPending || twintAmount <= 0}
                                    sx={{ flex: 1 }}
                                >
                                    {twintStatus.code === "PENDING" ? <CircularProgress size={20} /> : "Twint-Zahlung"}
                                </Button>
                            </Box>

                            {twintStatus.code === "PENDING" && (
                                <Button sx={{ mb: 2 }} color="warning" variant="outlined" size="small" onClick={retryTwintPayment}>Twint erneut versuchen</Button>
                            )}
                        </Box>
                    </Collapse>
                </>
                }
                <Box display={"flex"} gap={2} pb={2}>
                    <CashAmountInput
                        label="BAR"
                        onChange={(val) => setGivenCash(val / 100)}
                    />
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleFinish}
                        disabled={!isComplete}
                        sx={{ flex: 1 }}
                    >
                        Bar erhalten
                    </Button>
                </Box>
            </Box>

            <Box mt={2}>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={2}>Gesamtbetrag</TableCell>
                            <TableCell>{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(amount)}</TableCell>
                        </TableRow>
                        <TableRow sx={{borderBottom: 'solid 2px black'}}>
                            <TableCell colSpan={2}>
                                Bereits bezahlt:
                                {( paidTwint > 0 || paidCard > 0) && 
                                <Box sx={{fontSize:'12px', color: '#666'}}>
                                {paidTwint > 0 &&
                                    <Box>
                                    Twint: {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(paidTwint)}
                                    </Box>
                                }
                                {paidCard > 0 &&
                                    <Box>
                                    Karte: {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(paidCard)}
                                    </Box>
                                }
                                </Box>
                            }
                            </TableCell>
                            <TableCell>{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(paidTotal)}</TableCell>
                        </TableRow>

                        <TableRow sx={{borderBottom: 'solid 2px black'}}>
                            <TableCell colSpan={2}>Offener Barbetrag:</TableCell>
                            <TableCell sx={{fontWeight: isComplete ? 'normal' : 'bold'}}>
                                {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cashRequired)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={2}>Rückgeld:</TableCell>
                            <TableCell>{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(change)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={2}></TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                 
            </Box>

            <Box mt={2}>

            </Box>
        </Box>
    );
}
